import React, { useState, useEffect, useRef } from 'react';
import { useOrg, ORG_ID_STORAGE_KEY } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabaseClient';

export const OrgGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { orgs, currentOrg, setCurrentOrgId, loading } = useOrg();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [acceptingInvite, setAcceptingInvite] = useState(false);
  const inviteProcessed = useRef(false);

  // Handle ?invite=TOKEN in URL
  useEffect(() => {
    if (loading || inviteProcessed.current) return;

    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');
    if (!inviteToken) return;

    inviteProcessed.current = true;
    setAcceptingInvite(true);

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          setAcceptingInvite(false);
          return;
        }

        const res = await fetch('/api/invites/accept', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ token: inviteToken }),
        });

        const data = await res.json();

        // Clear the invite param from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        window.history.replaceState({}, '', url.toString());

        if (res.ok) {
          if (data.alreadyMember) {
            toast('You are already a member of this organization.', 'info');
          } else {
            toast(`You've joined ${data.orgName || 'the organization'} as ${data.role}!`, 'success');
          }
          // Set the joined org as current so OrgContext selects it after reload
          if (data.orgId) {
            localStorage.setItem(ORG_ID_STORAGE_KEY, data.orgId);
          }
          // Reload to pick up the new org membership
          window.location.href = url.toString();
        } else {
          toast(data.error || 'Failed to accept invite', 'error');
          setAcceptingInvite(false);
        }
      } catch {
        toast('Failed to accept invite', 'error');
        setAcceptingInvite(false);
      }
    })();
  }, [loading, toast]);

  if (loading || acceptingInvite) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400 text-lg">{acceptingInvite ? 'Accepting invitation...' : 'Loading organization...'}</div>
      </div>
    );
  }

  // User has orgs but none selected (multiple orgs) — show picker
  if (orgs.length > 1 && !currentOrg) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-2">Select Organization</h2>
          <p className="text-slate-400 mb-6 text-sm">Choose which organization to work in.</p>
          <div className="space-y-2">
            {orgs.map((org) => (
              <button
                key={org.id}
                onClick={() => setCurrentOrgId(org.id)}
                className="w-full text-left p-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 border border-slate-600 transition-colors"
              >
                <div className="font-semibold text-white">{org.name}</div>
                <div className="text-xs text-slate-400 mt-1">Role: {org.role}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No orgs — invite-only gating
  if (orgs.length === 0) {
    const hasInvite = new URLSearchParams(window.location.search).has('invite');

    if (hasInvite) {
      // Invite is present — the useEffect above will handle acceptance
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-slate-400 text-lg">Processing invitation...</div>
        </div>
      );
    }

    // No invite, no orgs — show "Invitation Required" screen
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full shadow-2xl text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Invitation Required</h2>
          <p className="text-slate-400 mb-6 text-sm">
            Persona Builder is invite-only. You need an invitation link from an existing team to get started.
          </p>
          <p className="text-slate-500 text-xs mb-6">
            Signed in as {user?.email}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 px-6 py-3 bg-slate-700 text-white font-semibold rounded-xl hover:bg-slate-600 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => signOut()}
              className="flex-1 px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Org is selected — render children
  return <>{children}</>;
};
